/** F6 · Studio (screens §F6): Debrief and Finale tabs beside the live room-screen preview, plus the Assessment tab (F7). */
import { useState } from "react";
import type { RoomScreenShow } from "@bsim/contracts";
import { useQueryClient } from "@tanstack/react-query";
import type { ApiClient } from "../api/client";
import { useRoute } from "../api/context";
import { navigate, useLocation } from "../shell/history";
import { GxCard, GxWaiting } from "../theme/primitives";
import { AssessmentReview } from "./AssessmentReview";
import { DebriefTab, DECK_WAIT } from "./DebriefTab";
import { FinaleTab } from "./FinaleTab";
import { UpNext } from "./UpNext";
import { RoomPreview } from "./RoomPreview";
import { surpriseLive } from "./derive";
import { useAct } from "./useAct";
import { useBoardData } from "./useBoardData";
import { GxNotes } from "../theme/GxNotes";
import { GxTabs } from "../theme/GxTabs";

/** What a Project press is called when it lands, in the facilitator's words. */
const SHOWN_WORDS: Readonly<Record<string, string>> = { clock: "the clock", surprise: "the surprise", finale: "the finale stage", slide: "the debrief slide", pick: "the pick", standings: "the standings" };

export type StudioTab = "debrief" | "finale" | "assessment";

export function StudioPage({ cohortId, api, tab: fixedTab }: { readonly cohortId: string; readonly api: ApiClient; readonly tab?: StudioTab }) {
  const act = useAct(api);
  const queryClient = useQueryClient();
  const { search } = useLocation();
  const { board } = useBoardData(cohortId);
  const room = useRoute("roomScreenGet", { cohortId });
  // What the last Project press put on the projector, said beside the buttons: the preview alone gives no sign that a press landed.
  const [projected, setProjected] = useState<string | null>(null);
  if (board === undefined) return <GxWaiting what="the studio" words={DECK_WAIT} testId="studio.skeleton" height={380} />;

  const live = surpriseLive(board.surprise);
  const lastRound = board.rounds.at(-1);
  const finaleOpen = lastRound?.state === "released" || board.cohort.finaleStage !== "notStarted";
  const wanted: StudioTab = fixedTab ?? (new URLSearchParams(search).get("tab") === "finale" ? "finale" : "debrief");
  const tab: StudioTab = wanted === "finale" && !finaleOpen ? "debrief" : wanted;
  const base = `/f/${cohortId}/studio`;
  const tabs: readonly (readonly [StudioTab, string, string])[] = [
    ["debrief", "Debrief", base], ...(finaleOpen ? [["finale", "Finale", `${base}?tab=finale`] as const] : []), ["assessment", "Assessment", `${base}/assessment`],
  ];

  // Why Show surprise is greyed, in words beside it.
  const offWhy = live || tab === "finale" ? undefined : board.surprise === null ? "No surprise is waiting or running right now (one that already ran is not shown again here), so there is nothing to show." : "A surprise is held and the teams have not seen it yet. Release it from the board, then show it.";
  const project = async (show: RoomScreenShow) => {
    const res = await act("roomScreenSet", { cohortId }, { show });
    if (res.ok) { queryClient.setQueryData(["roomScreenGet", { cohortId }], res.value); setProjected(`Now on the projector: ${SHOWN_WORDS[show.kind] ?? "the room screen"}.`); }
  };

  return (
    <div className="gx-fac-page" data-testid="studio">
      <div className="gx-fac-head">
        <div><div className="gx-eyebrow">{board.cohort.name}</div><h1 className="gx-h1">Studio</h1></div>
        <GxTabs label="Studio" active={tab} tabs={tabs.map(([key, label]) => ({ id: key, label }))} onSelect={(id) => navigate(tabs.find(([key]) => key === id)?.[2] ?? base)} />
      </div>
      {finaleOpen ? null : <div className="gx-fac-hint"><GxNotes testId="studio.finale.hint" notes={["The Finale tab opens once the last round is released."]} /></div>}
      {tab === "assessment" ? <AssessmentReview cohortId={cohortId} teams={board.teams} act={act} programId={board.cohort.mode === "solo" ? board.cohort.programId : null} /> : (
        <div className="gx-fac-studio">
          <div>
            {tab === "debrief" ? <DebriefTab cohortId={cohortId} board={board} room={room.data} act={act} project={(show) => void project(show)} /> : <FinaleTab cohortId={cohortId} act={act} projectFinale={() => void project({ kind: "finale" })} />}
          </div>
          <aside>
            <GxCard className="gx-fac-roomcard">
              <div className="gx-eyebrow" data-testid="room.nowLabel">On the projector now</div>
              {room.data === undefined ? <GxWaiting what="the room screen" height={220} /> : <RoomPreview view={room.data} />}
              <p className="gx-small gx-faint gx-fac-room-phone" data-testid="room.phoneNote">The projector preview is too small to read on a phone. Open the studio on a bigger screen to see it; Project and Show still work.</p>
              <div className="gx-fac-roomacts">
                <button type="button" className="gx-btn gx-btn--secondary" data-testid="room.showClock" onClick={() => void project({ kind: "clock" })}>Show clock</button>
                {/* "Show surprise" is enabled once the surprise is released (delivered, or answering): the room screen must never show a held card (screens §F1 acceptance 4). */}
                <button type="button" className="gx-btn gx-btn--secondary" data-testid="room.showSurprise" disabled={!live} title={offWhy} onClick={() => void project({ kind: "surprise" })}>Show surprise</button>
              </div>
              {tab === "finale" ? null : <UpNext cohortId={cohortId} board={board} room={room.data} project={(show) => void project(show)} />}
              {projected === null ? null : <p className="gx-small gx-fac-roomnote" role="status" data-testid="room.projected">{projected}</p>}
              {offWhy === undefined ? null : <p className="gx-small gx-faint gx-fac-roomnote" data-testid="room.showSurprise.why">{offWhy}</p>}
            </GxCard>
            {tab === "finale" && room.data !== undefined && room.data.show.kind !== "finale" ? <div className="gx-small gx-faint gx-fac-roomnote" data-testid="room.followNote">Press 'Project finale' to put this stage on the projector.</div> : null}
            <div className="gx-small gx-faint gx-fac-roomnote">This is a preview of the projector; the room screen shows what is previewed here.</div>
          </aside>
        </div>
      )}
    </div>
  );
}
